import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AccountProfile } from '@/lib/types'
import {
  getAccountProfile,
  updateAccountProfile,
  getRecentActivities,
  getVoiceSettings,
  getBirthdaySettings,
  updateBirthdaySettings,
  getReviewSettings,
  updateReviewSettings,
  listSubscriptionPlans,
  createSubscriptionPlan,
  updateSubscriptionPlan,
  deleteSubscriptionPlan,
  listCustomerSubscriptions,
  updateVoiceSetting,
  listCatalog,
  getNotificationSettings,
  updateNotificationSettings,
  getCallHandling,
  updateCallHandling,
  uploadAccountLogo,
  getPortRequest,
} from './api'
import type { NotificationSettings, UpdateNotificationSettingsInput } from './api'
import { invalidateSetupChecklist } from '@/modules/setup-checklist/hooks'

export function useAccount(accountId: string) {
  return useQuery({
    queryKey: ['account', accountId],
    queryFn: () => getAccountProfile(accountId),
    enabled: !!accountId,
  })
}

/**
 * The account's current number transfer, or `null` when they never ported.
 *
 * A carrier port runs for days, and its status only moves when Twilio calls our
 * webhook or the worker's scheduled poll reconciles it — so there is nothing to
 * gain from polling hard here. A 5-minute stale time keeps a returning owner's
 * view fresh without hammering the endpoint on every navigation.
 */
export function usePortRequest(accountId: string) {
  return useQuery({
    queryKey: ['port-request', accountId],
    queryFn: () => getPortRequest(accountId),
    enabled: !!accountId,
    staleTime: 5 * 60_000,
  })
}

/**
 * Pulse intake timeline + LLM recap. Polls every 30s (and refetches on window
 * focus via the global QueryClient) so a freshly regenerated summary surfaces
 * without a manual refresh.
 */
export function useRecentActivities(accountId: string) {
  return useQuery({
    queryKey: ['recent-activities', accountId],
    queryFn: () => getRecentActivities(accountId),
    enabled: !!accountId,
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}

/** The account's service/product catalog (account_services) — the invoice
 *  editor's item finder reads it. CatalogEditor manages its own local state
 *  and doesn't invalidate this key; refetch-on-focus covers staleness. */
export function useCatalog(accountId: string) {
  return useQuery({
    queryKey: ['catalog', accountId],
    queryFn: () => listCatalog(accountId),
    enabled: !!accountId,
  })
}

/** Voice-persona voice picks (Lyra/Aria TTS voice) for the settings page. */
export function useVoiceSettings(accountId: string) {
  return useQuery({
    queryKey: ['account', accountId, 'voice-settings'],
    queryFn: () => getVoiceSettings(accountId),
    enabled: !!accountId,
  })
}

export function useUpdateVoiceSetting() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      accountId,
      personaType,
      voice,
      voiceModel,
    }: {
      accountId: string
      personaType: 'lyra' | 'aria'
      voice: string
      /** Switch to another catalog model (Pro+ voice library). */
      voiceModel?: string
    }) => updateVoiceSetting(accountId, personaType, voice, voiceModel),
    onSuccess: (_, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: ['account', accountId, 'voice-settings'] })
    },
  })
}

/** Call handling — AI trust level + escalation contact + concierge coverage. */
export function useCallHandling(accountId: string) {
  return useQuery({
    queryKey: ['account', accountId, 'call-handling'],
    queryFn: () => getCallHandling(accountId),
    enabled: !!accountId,
  })
}

export function useUpdateCallHandling(accountId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (updates: Parameters<typeof updateCallHandling>[1]) =>
      updateCallHandling(accountId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', accountId, 'call-handling'] })
    },
  })
}

/** Birthday-text settings (Concierge growth feature). */
export function useBirthdaySettings(accountId: string) {
  return useQuery({
    queryKey: ['account', accountId, 'birthday-settings'],
    queryFn: () => getBirthdaySettings(accountId),
    enabled: !!accountId,
  })
}

export function useUpdateBirthdaySettings(accountId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (updates: Parameters<typeof updateBirthdaySettings>[1]) =>
      updateBirthdaySettings(accountId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', accountId, 'birthday-settings'] })
    },
  })
}

/** Review follow-up settings (Concierge growth feature). */
export function useReviewSettings(accountId: string) {
  return useQuery({
    queryKey: ['account', accountId, 'review-settings'],
    queryFn: () => getReviewSettings(accountId),
    enabled: !!accountId,
  })
}

export function useUpdateReviewSettings(accountId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (updates: Parameters<typeof updateReviewSettings>[1]) =>
      updateReviewSettings(accountId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', accountId, 'review-settings'] })
    },
  })
}

/** Customer subscription plans (Concierge growth feature). */
export function useSubscriptionPlans(accountId: string) {
  return useQuery({
    queryKey: ['account', accountId, 'subscription-plans'],
    queryFn: () => listSubscriptionPlans(accountId),
    enabled: !!accountId,
  })
}

export function useCustomerSubscriptions(accountId: string) {
  return useQuery({
    queryKey: ['account', accountId, 'customer-subscriptions'],
    queryFn: () => listCustomerSubscriptions(accountId),
    enabled: !!accountId,
  })
}

export function useSubscriptionPlanMutations(accountId: string) {
  const queryClient = useQueryClient()
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['account', accountId, 'subscription-plans'] })
  const create = useMutation({
    mutationFn: (plan: Parameters<typeof createSubscriptionPlan>[1]) =>
      createSubscriptionPlan(accountId, plan),
    onSuccess: invalidate,
  })
  const update = useMutation({
    mutationFn: ({ planId, updates }: { planId: string; updates: Parameters<typeof updateSubscriptionPlan>[2] }) =>
      updateSubscriptionPlan(accountId, planId, updates),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: (planId: string) => deleteSubscriptionPlan(accountId, planId),
    onSuccess: invalidate,
  })
  return { create, update, remove }
}

/** Full-transparency notification prefs (/settings/preferences). */
export function useNotificationSettings(accountId: string) {
  return useQuery({
    queryKey: ['account', accountId, 'notification-settings'],
    queryFn: () => getNotificationSettings(accountId),
    enabled: !!accountId,
  })
}

/**
 * Optimistic per-toggle saves: each click PUTs one small delta, the cache
 * flips immediately, and we only reconcile with the server once the last
 * in-flight mutation settles (so rapid toggling never flickers backwards).
 */
export function useUpdateNotificationSettings(accountId: string) {
  const queryClient = useQueryClient()
  const queryKey = ['account', accountId, 'notification-settings']
  const mutationKey = ['update-notification-settings', accountId]
  return useMutation({
    mutationKey,
    mutationFn: (updates: UpdateNotificationSettingsInput) =>
      updateNotificationSettings(accountId, updates),
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<NotificationSettings>(queryKey)
      if (previous) {
        const prefs = { ...previous.prefs }
        for (const p of updates.prefs ?? []) {
          const current = prefs[p.key]
          if (!current) continue
          prefs[p.key] = {
            sms: p.sms ?? current.sms,
            email: p.email ?? current.email,
          }
        }
        queryClient.setQueryData<NotificationSettings>(queryKey, {
          ...previous,
          prefs,
          digest: {
            enabled: updates.digestEnabled ?? previous.digest.enabled,
            hourLocal: updates.digestHourLocal ?? previous.digest.hourLocal,
          },
          receiveSmsAlerts: updates.receiveSmsAlerts ?? previous.receiveSmsAlerts,
        })
      }
      return { previous }
    },
    onError: (_err, _updates, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous)
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey }) === 1) {
        queryClient.invalidateQueries({ queryKey })
      }
    },
  })
}

export function useUpdateAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      accountId,
      updates,
    }: {
      accountId: string
      updates: Partial<AccountProfile>
    }) => updateAccountProfile(accountId, updates),
    onSuccess: (_, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: ['account', accountId] })
      // The profile row of the sidebar setup checklist is derived from these
      // fields; without this it stays unticked until a reload (#700).
      invalidateSetupChecklist(queryClient)
    },
  })
}

/** Upload once, then make the new account picture visible on every surface. */
export function useUploadAccountLogo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      accountId,
      data,
      contentType,
    }: {
      accountId: string
      data: string
      contentType: string
    }) => uploadAccountLogo(accountId, data, contentType),
    onSuccess: ({ logoUrl }, { accountId }) => {
      queryClient.setQueryData<AccountProfile>(['account', accountId], (current) =>
        current ? { ...current, logoUrl } : current,
      )
      queryClient.invalidateQueries({ queryKey: ['account', accountId] })
    },
  })
}
